/**
 * Transaction Service Module
 * Provides functionality for managing financial transactions including creation,
 * retrieval, updates, and deletion of transactions. Also handles transaction-related
 * calculations and file attachments.
 */

import Parse from "./parse.service";
import { auth, accountAPI } from "./index";

const Transaction = Parse.Object.extend("Transaction");

/**
 * Rolls back a transaction and associated account balance changes
 * @private
 * @param {Parse.Object} transaction - Transaction object to roll back
 * @param {Parse.Object} myAccount - Account object affected by the transaction
 * @param {string} _account - Account name
 * @param {number} currentAccountBalance - Original account balance before transaction
 * @param {number} amount - Transaction amount to roll back
 */
const transactionRollBack = async (transaction, myAccount, _account, currentAccountBalance, amount) => {
  try {
    // Rollback the transaction if it was saved
    if (transaction.id) {
      await transaction.destroy();
      console.log("Rolling back transaction...");
    }
    // Rollback balance update
    if ((_account && myAccount) && currentAccountBalance !== 0) {
      const updatedAccount = await accountAPI.getAccountByName(_account);
      if (updatedAccount && updatedAccount.get("balance") !== currentAccountBalance) {
        updatedAccount.increment("balance", -amount);
        await updatedAccount.save();
      }
    }
  } catch (rollbackError) {
    console.error("Rollback failed: ", rollbackError);
  }
}

/**
 * Creates a new transaction and updates associated account balance
 * @param {string} _account - Account name
 * @param {number} amount - Transaction amount
 * @param {string} detail - Transaction details/description
 * @param {string} attachment - URL or reference to attached file
 * @param {Date} date - Transaction date
 * @returns {Promise<Parse.Object>} Created transaction object
 * @throws {Error} If transaction creation fails
 */
export const transaction = async (_account, amount, detail, attachment, date, isCash) => {
  let currentAccountBalance = 0;
  let transactionSuccess = false;
  let myAccount = null;
  const transaction = new Transaction();

  try {
    //Fetch account if exists otherwise create account
    if (_account) {
      myAccount = await accountAPI.getAccountByName(_account);

      if (!myAccount) {
        myAccount = await accountAPI.createAccount(_account, "");
        if (!myAccount) throw new Error("New account creation failed.");
      }
      currentAccountBalance = myAccount.get("balance") || 0;
      transaction.set("accountId", myAccount);
    }

    // Set transaction details
    transaction.set("amount", amount);
    transaction.set("detail", detail);
    transaction.set("date", date);
    transaction.set("cancelled", false);
    transaction.set("userId", auth.fetchUser());
    transaction.set("attachment", attachment);
    transaction.set("type", isCash ? "CASH" : "CREDIT");

    // Perform atomic save of transaction & account balance
    if (myAccount) {
      myAccount.increment("balance", amount);
      const res = await Parse.Object.saveAll([transaction, myAccount]); // Atomic transaction save
      const accountblcupdated = res[1]?.get("balance");
      console.log("accountblcafterupdate ", accountblcupdated);
      transactionSuccess = true;
      return transaction;
    }
    return await transaction.save()
  } catch (error) {
    console.error("Transaction failed. Rolling back...", error);
    await transactionRollBack(transaction, myAccount, _account, currentAccountBalance, amount);
    if (error.code === 122) {
      throw new Error("Filename contains invalid characters");
    }
    throw new Error("Transaction was not successful.")

  }
};

/**
 * Uploads a file to Parse server
 * @param {File} file - File to upload
 * @returns {Promise<Parse.File|null>} Uploaded file object or null if upload fails
 */
export const uploadFile = async (file) => {
  if (file) {
    try {
      const name = file.name;
      var parseFile = new Parse.File(name, file);
      var result = await parseFile.save();
      return result;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  }
}

/**
 * Retrieves transactions for a specific account within a given month
 * @param {string} accountId - ID of the account
 * @param {Date} currentDate - Date object representing the month to query
 * @returns {Promise<Parse.Object[]>} Array of transaction objects
 */
export const getTransactionsByAccountIdWithMonth = async (accountId, currentDate) => {
  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(),
    1, 0, 0, 0, 0);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1,
    0, 23, 59, 59, 999);

  const account = await accountAPI.getAccountById(accountId);
  const query = new Parse.Query(Transaction);

  query.limit(10000);
  // query.equalTo("userId", auth.fetchUser());
  query.equalTo("accountId", account);

  query.greaterThanOrEqualTo("date", startOfMonth);
  query.lessThanOrEqualTo("date", endOfMonth);
  query.ascending("date");
  query.exclude("userId");
  const results = await query.find();
  return results;
}

/**
 * Retrieves all transactions for a specific date
 * @param {Date} date - Date to query transactions for
 * @returns {Promise<Parse.Object[]>} Array of transaction objects
 */
export const getTransactionsByDate = async (date) => {
  const query = new Parse.Query(Transaction);
  const firstDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1,
    23, 59, 59, 999);
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
    23, 59, 60, 0);
  // query.include("accountId");
  query.limit(1000000);

  query.equalTo("userId", auth.fetchUser());
  query.greaterThan("date", firstDate);
  query.lessThan("date", nextDate);
  query.equalTo("type", "CASH");
  query.exclude("userId");
  return await query.find();
}

/**
 * Retrieves all transactions for a specific account
 * @param {string} id - Account ID
 * @returns {Promise<Parse.Object[]>} Array of transaction objects
 */
export const getTxnsByAccountId = async (id) => {
  const account = await accountAPI.getAccountById(id);
  const query = new Parse.Query(Transaction);
  query.limit(10000);
  query.ascending("date");
  query.equalTo("userId", auth.fetchUser());
  query.equalTo("accountId", account);
  query.exclude("userId");
  return await query.find();
}

/**
 * Retrieves a specific transaction by ID
 * @param {string} id - Transaction ID
 * @returns {Promise<Parse.Object|undefined>} Transaction object if found
 */
export const getTransactionById = async (id) => {
  const query = new Parse.Query(Transaction);
  query.equalTo("userId", auth.fetchUser());
  query.equalTo("objectId", id);
  query.limit(1);
  const results = await query.find();
  return results[0];
}

/**
 * Updates an existing transaction and adjusts account balance
 * @param {string} id - Transaction ID
 * @param {number} _amount - New transaction amount
 * @param {string} _detail - New transaction details
 * @param {Date} date - New transaction date
 * @returns {Promise<Parse.Object>} Updated transaction object
 */
export const updateTransaction = async (id, _amount, _detail, date, _attachment) => {
  const transaction = await getTransactionById(id);
  const oldamount = transaction.get("amount");
  const account_object = transaction.get("accountId");
  const diffamount = _amount - oldamount;
  transaction.set("amount", _amount);
  if (account_object) {
    const accountId = account_object.id;
    await accountAPI.updateBalance(accountId, diffamount);
  }
  transaction.set("detail", _detail);
  transaction.set("date", date);
  transaction.set("attachment", _attachment);
  return transaction.save();
}

/**
 * Marks a transaction as cancelled and reverses the account balance
 * @param {string} id - Transaction ID
 * @returns {Promise<Parse.Object>} Updated transaction object
 */
export const deleteTransaction = async (id) => {
  const txn = await getTransactionById(id);
  txn.set("cancelled", true);
  const account_object = txn.get("accountId");
  if (account_object) {
    const accountId = account_object.id;
    accountAPI.updateBalance(accountId, -txn.get("amount"));
  }
  return txn.save();
}

/**
 * Calculates the sum of all transactions up to a specific date
 * @param {Date} date - Date to sum transactions up to
 * @returns {Promise<number>} Sum of transaction amounts
 */
export const sumTillDate = async (date) => {
  const query = new Parse.Query(Transaction);
  query.limit(10000);
  query.equalTo("userId", auth.fetchUser());
  query.lessThan("date", date);
  query.equalTo("cancelled", false);
  query.exclude("userId");
  query.equalTo("type", "CASH");
  const results = await query.find();
  return results.reduce((sum, result) => sum + result.get("amount"), 0);
}

/**
 * Calculates the sum of all transactions for a specific account up to a date
 * @param {Date} date - Date to sum transactions up to
 * @param {string} id - Account ID
 * @returns {Promise<number>} Sum of transaction amounts
 */
export const sumTillDateByAccount = async (date, id) => {
  const account = await accountAPI.getAccountById(id);
  if (!account) return 0;
  const query = new Parse.Query(Transaction);
  query.limit(10000);

  query.equalTo("accountId", account);
  query.lessThanOrEqualTo("date", date);
  query.equalTo("cancelled", false);
  query.exclude("userId");
  const results = await query.find();
  if (results) {
    var sum = 0;
    results.map((result) => (sum += result.get("amount")));
    return sum;
  }
  return 0;
}

export const dailyCashInOut = async (date, type) => {
  const query = new Parse.Query(Transaction);
  const firstDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1,
    23, 59, 59, 999);
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
    23, 59, 60, 0);
  // query.include("accountId");
  query.limit(10000);

  query.equalTo("userId", auth.fetchUser());
  query.greaterThan("date", firstDate);
  query.lessThan("date", nextDate);
  query.equalTo("cancelled", false);
  if (type == "in") {
    query.greaterThan("amount", 0);
  } else if (type == "out") {
    query.lessThan("amount", 0);
  }

  const results = await query.find();
  if (results) {
    var sum = 0;
    results.map((result) => (sum += result.get("amount")));
    return sum;
  }
  return 0;
}

export const monthlyCashInOut = async (accountId, currentMonth, type) => {
  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(),
    1, 0, 0, 0, 0);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1,
    0, 23, 59, 59, 999);

  const account = await accountAPI.getAccountById(accountId);
  const query = new Parse.Query(Transaction);

  query.limit(10000);
  query.equalTo("userId", auth.fetchUser());
  query.equalTo("accountId", account);
  query.equalTo("cancelled", false);
  query.greaterThanOrEqualTo("date", startOfMonth);
  query.lessThanOrEqualTo("date", endOfMonth);
  if (type == "in") {
    query.greaterThan("amount", 0);
  } else if (type == "out") {
    query.lessThan("amount", 0);
  }

  const results = await query.find();
  if (results) {
    var sum = 0;
    results.map((result) => (sum += result.get("amount")));
    return sum;
  }
  return 0;
}